import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import type { DirectedStyle, DirectedStylePreview } from 'fym-shared/src/music24/directedVideoApi';
import type { MusicVideoEffectFamily } from 'fym-shared/src/music24/musicVideoEffects';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../music24/components/primitives/SMLButton';
import { storyMessages as messages } from '../messages/storyMessages';
import { useDirectedCastNames } from '../useDirectedCastNames';
import { EffectFamilyChips } from './director/EffectFamilyChips';
import { StylePreviewPicture } from './StylePreviewPicture';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the director steps; each part uses its own slice of it */
import styles from './DirectorSteps.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface StyleCardProps {
  style: DirectedStyle;
  /** "Style 1", or "Your own". */
  label: string;
  /**
   * The preview still of the style (owner 2026-10-19), or undefined. The card shows it only for the
   * look it was made for: an edited draft gets none.
   */
  preview: DirectedStylePreview | undefined;
  isOn: boolean;
  isDim: boolean;
  isEdited: boolean;
  disabled: boolean;
  onPick: () => void;
  onEdit: () => void;
  /** A family chip was switched: the card is picked with the new families. */
  onFamiliesChange: (families: MusicVideoEffectFamily[]) => void;
}

/**
 * One way to film the story (design d1): the preview still of the style (in the place of the graded
 * cover of the design), then camera, colour and light, texture and pacing, each label above its
 * text (owner fix 2026-10-19), the effect families as chips that switch on and off, then Pick and
 * Edit.
 */
const StyleCardComponent: React.FC<StyleCardProps> = ({
  style,
  label,
  preview,
  isOn,
  isDim,
  isEdited,
  disabled,
  onPick,
  onEdit,
  onFamiliesChange,
}) => {
  const intl = useIntl();
  const { named } = useDirectedCastNames();
  const rows = [
    { key: 'camera', name: messages.camera, value: style.camera },
    { key: 'colour', name: messages.colourLight, value: style.colourAndLight },
    { key: 'texture', name: messages.texture, value: style.texture },
    { key: 'pacing', name: messages.pacing, value: style.pacing },
  ].filter((row) => row.value !== '');
  return (
    <div
      className={clsx(styles.option, isOn && styles.optionOn, isDim && styles.optionDim)}
      data-testid={`style-${style.id}`}
    >
      <StylePreviewPicture preview={isEdited ? undefined : preview} title={named(style.title)} />
      <div className={styles.optionHead}>
        <span className={styles.optionNumber}>{label}</span>
        {isEdited && <span className={styles.edited}>{intl.formatMessage(messages.edited)}</span>}
        <button
          type="button"
          role="radio"
          aria-checked={isOn}
          aria-label={label}
          className={clsx(styles.radio, isOn && styles.radioOn)}
          disabled={disabled}
          onClick={onPick}
        />
      </div>
      <div className={styles.optionTitle}>{named(style.title)}</div>
      {!isDim && (
        <div className={styles.styleRows}>
          {rows.map((row) => (
            <div key={row.key} className={styles.styleRow}>
              <span className={styles.styleKey}>{intl.formatMessage(row.name)}</span>
              <span className={styles.styleValue}>{named(row.value)}</span>
            </div>
          ))}
        </div>
      )}
      {!isDim && (
        <div className={styles.styleFamilies}>
          <span className={styles.styleKey}>{intl.formatMessage(messages.effects)}</span>
          <EffectFamilyChips
            families={style.effectFamilies}
            label={intl.formatMessage(messages.effectsOf, { style: label })}
            disabled={disabled}
            withHints
            onChange={onFamiliesChange}
          />
          <span className={styles.styleFamiliesHint}>{intl.formatMessage(messages.familiesHint)}</span>
        </div>
      )}
      {!isDim && (
        <div className={styles.optionActions}>
          <SMLButton className={styles.small} disabled={disabled} onClick={onPick}>
            {intl.formatMessage(isOn ? messages.picked : messages.pick)}
          </SMLButton>
          <SMLButton className={clsx(styles.small, styles.ghost)} disabled={disabled} onClick={onEdit}>
            {intl.formatMessage(messages.edit)}
          </SMLButton>
        </div>
      )}
    </div>
  );
};

export const StyleCard = TypedMemo(StyleCardComponent);
